import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setTradeData, setActiveTrade } from "@/store/globalSlice"
import { allMapsImageData } from "@/utils/data"
import classNames from "classnames"
import gsap from "gsap"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import propertyCardMovementAudioFile from "@/audio/property-card-movement.mp3"

import redArrowImage from "@/images/arrow-red-right.webp"
import { getUserCreds, toggleItemInArray } from "@/utils/utils"
import {
  getTradeCardSelectionType,
  isTradeDraftEditable,
} from "@/utils/tradeHelpers"
import { getPlayerOrderFromPlayerId } from "@/utils/animationHelpers"

export interface TradeScreenPropertyCardProps {
  property: ICell
  group: IPropertyGroup
  height?: number
  width?: number
}

const TradeScreenPropertyCard: React.FC<TradeScreenPropertyCardProps> = ({
  property,
  group,
}) => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const tradeData = useAppSelector((state) => state.global.tradeData)
  const myRef = useRef<HTMLDivElement | null>(null)
  const mapImageData = allMapsImageData.find(
    (imageData) => imageData.mapId === myGame?.settings.mapId
  )

  const imageData = mapImageData?.cellImages.find(
    (cellImage) => cellImage.order === property.order
  )

  const myOwner = myGame?.situation?.players.find((player) =>
    player.properties.includes(property.order)
  ) as IPlayer

  const activeTrade = tradeData.activeTrade
  const viewerOrder = getPlayerOrderFromPlayerId(
    myGame as IGameDetails,
    getUserCreds().email
  )
  // Always read the trade from the offering player's side, whoever is looking.
  // getTradeCardSelectionType swaps give/take depending on the order it is
  // handed, so rendering this viewer-relative used to mirror the arrows and the
  // red/green colours for the offered player only — the same trade looked like
  // two different trades depending on whose screen you were on. A trade is only
  // ever composed by the offering player, so their perspective is the one
  // canonical reading, and this leaves what the composer sees unchanged.
  const myPlayerOrder = activeTrade?.offeringPlayerOrder ?? viewerOrder
  const selectionType = getTradeCardSelectionType(
    tradeData,
    property,
    myPlayerOrder
  )
  const isSelected = selectionType === "give" || selectionType === "take"
  // Cards are only clickable while composing/editing an offer, never when
  // viewing an already-offered trade.
  const isEditable = isTradeDraftEditable(tradeData.status)

  // Which properties (if any) are already chosen on this card's side of the
  // trade. Used to fade the un-picked cards so the picked ones stand out
  // without anything being drawn over the card face.
  const ownerSelectedList =
    myOwner?.order === activeTrade?.offeringPlayerOrder
      ? activeTrade?.givenProperties
      : myOwner?.order === activeTrade?.offeredPlayerOrder
      ? activeTrade?.takenProperties
      : undefined
  const anySelectedInPanel = (ownerSelectedList?.length || 0) > 0
  const isDimmed = anySelectedInPanel && !isSelected

  const isGive = selectionType === "give"
  // Which way the property flows between the two side-by-side panels.
  const pointRight = isGive
    ? myPlayerOrder === activeTrade?.offeringPlayerOrder
    : myPlayerOrder === activeTrade?.offeredPlayerOrder
  return (
    <div
      onMouseEnter={() =>
        dispatch(setTradeData({ hoveredPropertyOrder: property.order }))
      }
      onMouseLeave={() =>
        dispatch(setTradeData({ hoveredPropertyOrder: undefined }))
      }
      onClick={() => {
        if (!isEditable) return
        if (
          myOwner.order === tradeData.activeTrade?.offeringPlayerOrder
        ) {
          dispatch(
            setActiveTrade({
              givenProperties: toggleItemInArray(
                tradeData.activeTrade.givenProperties,
                property.order
              ),
            })
          )
        }
        if (myOwner.order === tradeData.activeTrade?.offeredPlayerOrder) {
          dispatch(
            setActiveTrade({
              takenProperties: toggleItemInArray(
                tradeData.activeTrade.takenProperties,
                property.order
              ),
            })
          )
        }
      }}
      ref={myRef}
      key={property.order + (property.metadata?.dummySmallCard ? "dummy" : "")}
      data-testid={`trade.property-small-card-${property.order}`}
      id={
        property.metadata?.dummySmallCard
          ? `small-card-dummy-${property.order}`
          : `small-card-${property.order}`
      }
      // Size comes from custom properties set by the ancestor (see
      // useFitTradeCards) so the compact trade panel can shrink every card at
      // once to fit its height cap. The fallbacks are the original
      // `w-8 h-10 mb-2`, which is what every other caller still gets.
      style={{
        width: "var(--tc-w, 2rem)",
        height: "var(--tc-h, 2.5rem)",
        marginBottom: "var(--tc-gap, 0.5rem)",
      }}
      className={classNames(
        "small-card relative rounded-md border bg-clip-padding transition-all duration-150 hover:-translate-y-0.5 hover:z-10",
        {
          "cursor-pointer": isEditable,
          "cursor-default": !isEditable,
          "bg-white": !group.withStrip,
          "bg-primary": group.withStrip,
          "opacity-0 pointer-events-none": property.metadata?.dummySmallCard,
          "!border-red-400 ring-2 ring-red-400 ring-offset-2 ring-offset-gray-900 shadow-[0_0_16px_rgba(248,113,113,0.7)] -translate-y-0.5 scale-[1.06] z-10 give":
            selectionType === "give",
          "!border-emerald-400 ring-2 ring-emerald-400 ring-offset-2 ring-offset-gray-900 shadow-[0_0_16px_rgba(52,211,153,0.7)] -translate-y-0.5 scale-[1.06] z-10 take":
            selectionType === "take",
          "border-black/70 shadow-md hover:shadow-lg normal":
            selectionType !== "give" && selectionType !== "take",
          "opacity-35 hover:opacity-100": isDimmed,
        }
      )}
    >
      {group.withStrip && (
        <div
          className="border-b-black border-b w-full rounded-t-md"
          style={{
            backgroundColor: group.color,
            height: "var(--tc-strip, 0.5rem)",
          }}
        ></div>
      )}
      {imageData?.smallCardImage && (
        <div className="w-full h-full p-[2px] py-[3px]">
          <div
            className="h-full w-full flex items-center justify-center"
            // Pulls the artwork back up over the colour strip, so it has to
            // track the strip's height rather than staying a flat -0.5rem.
            style={{ marginTop: "calc(var(--tc-strip, 0.5rem) * -1)" }}
          >
            <img src={imageData.smallCardImage} className="h-auto w-1/2" />
          </div>
        </div>
      )}
      {/* Direction arrow centered on the card. */}
      {isSelected && (
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 leading-none">
          <i
            style={{ fontSize: "var(--tc-arrow, 1.125rem)" }}
            className={classNames(
              "fa-solid drop-shadow-[0_1px_1px_rgba(0,0,0,0.85)]",
              isGive ? "text-red-500" : "text-emerald-400",
              pointRight ? "fa-arrow-right-long" : "fa-arrow-left-long"
            )}
          ></i>
        </div>
      )}
    </div>
  )
}

export default TradeScreenPropertyCard
